import * as React from 'react';
import cx from 'classnames';
import { ColumnOrderState } from '@tanstack/react-table';
import { Table as TableType } from '@tanstack/table-core/build/lib/types';

import { NewTableColumn, NewTableColumnId, NewTableDefaultDataItem, NewTableGetId, NewTableLoadMetadata, NewTableLoadMetadataSorting, NewTableLoading } from '../../new-table.types';
import { Header } from './components/header';
import { Body } from './components/body';

import './table-view.styles.scss';
import { Loading } from '@common/components/loading';
import { Flex } from '@common/components/Flex';

export type NewTableClasses = Partial<{
  root: string;
}>;

export type TableViewProps = {
  rootRef: React.MutableRefObject<HTMLDivElement | undefined>;
  classes?: NewTableClasses;
  columns: Array<NewTableColumn<NewTableDefaultDataItem>>;
  itemUniqProperty: string;
  noDataMessage: React.ReactNode;
  loadTableMetadata?: NewTableLoadMetadata;
  currentColumnResizeOffset?: number;
  model: TableType<NewTableDefaultDataItem>;
  loading: NewTableLoading;
  getId: NewTableGetId;
  onLazyLoad?: () => void;
  onColumnResizeEnd?: () => void;
  onRowContextMenu?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  isRowSelected?: (item: NewTableDefaultDataItem) => boolean;
  onRowClick?: (params: { item: NewTableDefaultDataItem; e: React.MouseEvent<HTMLTableRowElement> }) => void;
  onSort: (sorting: NewTableLoadMetadataSorting) => Promise<void> | void;
  onCellContextMenu?: (params: {
    columnId: NewTableColumnId;
    coords: {
      x: number;
      y: number;
    };
  }) => void;
  onHeaderContextMenu?: (params: {
    coords: {
      x: number;
      y: number;
    };
  }) => void;
  onColumnsOrderChange?: (columns: ColumnOrderState) => void;
};

export const TableView: React.FC<TableViewProps> = (props) => {
  const { rootRef, model, columns, loading, loadTableMetadata, classes, itemUniqProperty, noDataMessage, currentColumnResizeOffset, getId, onColumnsOrderChange, onLazyLoad, onColumnResizeEnd, onRowContextMenu, isRowSelected, onSort, onCellContextMenu, onHeaderContextMenu, onRowClick } = props;
  const rowModel = model.getRowModel();
  const dataLength = rowModel.rows.length;

  return (
    <div className={cx('NewTable', classes?.root)}>
      <div
        className="NewTable__scroll"
        ref={rootRef as React.MutableRefObject<HTMLDivElement>}
      >
        <table className="NewTable__inner" style={{ width: model.getCenterTotalSize() }}>
          <Header
            loading={loading}
            getId={getId}
            columns={columns}
            onColumnResizeEnd={onColumnResizeEnd}
            currentColumnResizeOffset={currentColumnResizeOffset}
            model={model.getHeaderGroups()}
            loadTableMetadata={loadTableMetadata}
            onSort={onSort}
            onHeaderContextMenu={onHeaderContextMenu}
            onCellContextMenu={onCellContextMenu}
            onColumnsOrderChange={onColumnsOrderChange}
          />
        { ( dataLength > 0) && (
            <Body
              rootRef={rootRef}
              getId={getId}
              onRowContextMenu={onRowContextMenu}
              isRowSelected={isRowSelected}
              itemUniqProperty={itemUniqProperty}
              onRowClick={onRowClick}
              model={rowModel}
              onLazyLoad={onLazyLoad}
            />
          )}
        </table>
      {(dataLength === 0 && !loading.list) && (
          <div className="NewTable__message">
            {noDataMessage}
          </div>
        )}
      </div>
      {loading.list && (
        <Flex width="100%" justify="center" alignItems="center" className="NewTable__loading-content">
          <Loading size={25}/>
        </Flex>
      )}
    </div>
  );
};